import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Collapse, Grid, IconButton,
  MenuItem, Paper, TextField, Tooltip, Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales';
import {
  FilterAltOffOutlined as FilterAltOffIcon,
  FilterAltOutlined as FilterAltIcon,
  RestartAlt as RestartAltIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import PartnerMainContainer from '../../../components/PartnerMainContainer';
import EvaluationService from '../../../services/EvaluationService';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import TitlePartner from '../component/Title';
import PartnerEvalution from '../Parterns/PartnerEvalution';
import EvalutionFiche from './EvalutionFiche';
import evaluationStyles from './evaluation.styles';
import {
  formatPartnerSource, normalizeEvalResult, normalizeFinancement, normalizeSituation,
} from '../../../helpers/PartnerHelper';
import usePartnerFilterConsultation from '../hooks/usePartnerFilterConsultation';
import { getSpmPID } from '../../../helpers/PeidGeneratorHelper';
import {
  partnerDataGridSx, partnerFilterButtonSx, partnerFilterFieldSx,
  partnerTableColors,
} from '../Parterns/partnerListTheme';

const INVALID_VALUES = ['nan', 'null', '-', 'undefined'];

const isValidValue = (value) => {
  if (value === null || value === undefined) return false;
  const text = String(value).trim();
  return text !== '' && !INVALID_VALUES.includes(text.toLowerCase());
};

const normalizeIdentity = (value) => String(value || '').normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim().toUpperCase();

const normalizeSituationValue = (value) => normalizeSituation(value) || 'ACHEVÉ';

const formatDate = (value) => {
  if (!isValidValue(value)) return '—';
  const parts = String(value).split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : String(value);
};

const formatCount = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return Number.isFinite(Number(value)) ? Number(value).toLocaleString('fr-FR') : '—';
};

const getEvaluationMeta = (value) => {
  const mapping = {
    'BONNE PERFORMANCE': { label: 'Bonne perf.', key: 'good' },
    'PERFORMANCE MOYENNE': { label: 'Moy.', key: 'average' },
    'PERFORMANCE INSUFFISANTE': { label: 'Insuffisant', key: 'poor' },
    ACCEPTABLE: { label: 'Acceptable', key: 'acceptable' },
  };
  return mapping[value] || { label: value || 'Manquant', key: value ? 'acceptable' : 'missing' };
};

const dedupeEvaluations = (evaluations) => {
  const unique = new Map();
  evaluations.forEach((evaluation) => {
    const key = `${normalizeEvalResult(evaluation.result)}|${evaluation.date || ''}|${evaluation.marche || ''}`;
    if (!unique.has(key)) unique.set(key, evaluation);
  });
  return Array.from(unique.values());
};

const deriveSituation = (values) => {
  if (values.has('EN COURS')) return 'EN COURS';
  if (values.has('RESILIE')) return 'RESILIE';
  return 'ACHEVÉ';
};

const Evaluation = () => {
  const classes = evaluationStyles();
  const [evaluationRows, setEvaluationRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [isPartnerEvaluationOpen, setIsPartnerEvaluationOpen] = useState(false);

  const defaultFilters = useMemo(() => ({
    directions: [], financements: [], situations: [], result: '', scopeSituation: '',
    contractFrom: '', contractTo: '', evalFrom: '', evalTo: '', search: '',
    sortBy: 'name', view: 'cards',
  }), []);
  const [filters, setFilters] = useState(defaultFilters);

  usePartnerFilterConsultation(filters, (nextFilters) => EvaluationService.getAllRows(nextFilters));

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    EvaluationService.getAllRows().then((result) => {
      if (!active) return;
      const data = Array.isArray(result?.data) ? result.data : [];
      setEvaluationRows(data);
      setLoadError(result?.status === 0 && data.length === 0 ? (result?.message || '') : '');
    }).catch(() => {
      if (active) setLoadError('Impossible de charger les évaluations.');
    }).finally(() => { if (active) setIsLoading(false); });
    return () => { active = false; };
  }, []);

  const baseRows = useMemo(() => {
    const partners = new Map();
    evaluationRows.filter((row) => isValidValue(row.partenaire)).forEach((row) => {
      const partnerName = String(row.partenaire).trim();
      const partnerId = Number(row.partenaire_id) || null;
      const spmId = Number(row.id_marche_spm) || null;
      const key = partnerId ? `PGI:${partnerId}` : `SPM:${normalizeIdentity(partnerName)}`;

      if (!partners.has(key)) {
        partners.set(key, {
          partenaireId: partnerId,
          firstSpmId: spmId,
          pid: row.pid || '',
          partenaire: partnerName,
          directions: new Set(), financements: new Set(), situations: new Set(),
          marches: new Map(), evaluations: [], contractPeriods: [],
          hasPgi: Boolean(partnerId), hasSpm: Boolean(spmId),
        });
      }

      const partner = partners.get(key);
      if (!partner.firstSpmId && spmId) partner.firstSpmId = spmId;
      partner.hasPgi = partner.hasPgi || Boolean(partnerId);
      partner.hasSpm = partner.hasSpm || Boolean(spmId);
      if (isValidValue(row.direction)) partner.directions.add(String(row.direction).trim());
      if (isValidValue(row.financement)) partner.financements.add(normalizeFinancement(row.financement));
      if (isValidValue(row.situation)) partner.situations.add(normalizeSituationValue(row.situation));

      const contractNumber = isValidValue(row.numeroMarche)
        ? String(row.numeroMarche).trim() : String(row.ref_num_contrat || '').trim();
      if (contractNumber) {
        const beneficiaries = Number(row.beneficiaires);
        partner.marches.set(contractNumber, Number.isFinite(beneficiaries) ? beneficiaries : null);
      }
      if (isValidValue(row.dateDebut) || isValidValue(row.dateFin)) {
        partner.contractPeriods.push({ dateDebut: row.dateDebut || '', dateFin: row.dateFin || '' });
      }

      const result = normalizeEvalResult(row.resultatEvaluation);
      if (result) {
        partner.evaluations.push({
          result, date: isValidValue(row.dateEvaluation) ? String(row.dateEvaluation) : null,
          marche: contractNumber, situation: normalizeSituationValue(row.situation),
        });
      }
    });

    return Array.from(partners.values()).map((partner) => {
      const evaluations = dedupeEvaluations(partner.evaluations).sort((left, right) =>
        String(right.date || '').localeCompare(String(left.date || '')));
      const marketBeneficiaries = Array.from(partner.marches.values());
      const hasBeneficiaries = marketBeneficiaries.some((value) => Number.isFinite(value));
      const sourceDb = partner.hasPgi && partner.hasSpm
        ? 'bdd suivi marché / bdd partenaire'
        : (partner.hasSpm ? 'bdd suivi marché' : 'bdd partenaire');
      const id = partner.partenaireId || `SPM-${partner.firstSpmId}`;

      return {
        id, partenaireId: partner.partenaireId,
        pid: partner.partenaireId
          ? (partner.pid || String(id))
          : (getSpmPID(partner.pid) || getSpmPID(id)),
        partenaire: partner.partenaire, partenaireLower: partner.partenaire.toLowerCase(),
        directions: Array.from(partner.directions).sort().join(', ') || '—',
        directionValues: Array.from(partner.directions),
        financementValues: Array.from(partner.financements),
        nbMarches: partner.marches.size,
        totalBeneficiaires: hasBeneficiaries
          ? marketBeneficiaries.reduce((sum, value) => sum + (Number(value) || 0), 0) : null,
        evaluation: evaluations[0]?.result || '',
        evaluationDate: evaluations[0]?.date || null,
        evalHistory: evaluations.slice(0, 6),
        situation: deriveSituation(partner.situations),
        contractPeriods: partner.contractPeriods,
        sourceDb, readOnly: !partner.partenaireId,
      };
    }).sort((left, right) => left.partenaire.localeCompare(right.partenaire, 'fr'));
  }, [evaluationRows]);

  const directionOptions = useMemo(() => [...new Set(baseRows.flatMap((row) => row.directionValues))].sort(), [baseRows]);
  const fundingOptions = useMemo(() => [...new Set(baseRows.flatMap((row) => row.financementValues))].sort(), [baseRows]);
  const situationOptions = useMemo(() => [...new Set(baseRows.map((row) => row.situation))].sort(), [baseRows]);

  const rows = useMemo(() => baseRows.filter((row) => {
    if (filters.search && !row.partenaireLower.includes(filters.search.toLowerCase().trim())) return false;
    if (filters.directions.length && !row.directionValues.some((value) => filters.directions.includes(value))) return false;
    if (filters.financements.length && !row.financementValues.some((value) => filters.financements.includes(value))) return false;
    if (filters.situations.length && !filters.situations.includes(row.situation)) return false;
    if (filters.scopeSituation && row.situation !== filters.scopeSituation) return false;
    if (filters.result === 'MANQUANT' && row.evaluation) return false;
    if (filters.result && filters.result !== 'MANQUANT' && row.evaluation !== filters.result) return false;
    if (filters.contractFrom || filters.contractTo) {
      const matchesPeriod = row.contractPeriods.some((period) => {
        if (filters.contractFrom && period.dateFin && period.dateFin < filters.contractFrom) return false;
        if (filters.contractTo && period.dateDebut && period.dateDebut > filters.contractTo) return false;
        return true;
      });
      if (!matchesPeriod) return false;
    }
    if (filters.evalFrom && (!row.evaluationDate || row.evaluationDate < filters.evalFrom)) return false;
    if (filters.evalTo && (!row.evaluationDate || row.evaluationDate > filters.evalTo)) return false;
    return true;
  }).sort((left, right) => {
    if (filters.sortBy === 'evalDate') return String(right.evaluationDate || '').localeCompare(String(left.evaluationDate || ''));
    if (filters.sortBy === 'nbMarches') return right.nbMarches - left.nbMarches;
    return left.partenaire.localeCompare(right.partenaire, 'fr');
  }), [baseRows, filters]);

  const missingEvaluationCount = baseRows.filter((row) => row.situation === 'EN COURS' && !row.evaluation).length;
  const activeFiltersCount = filters.directions.length + filters.financements.length + filters.situations.length
    + ['result', 'scopeSituation', 'contractFrom', 'contractTo', 'evalFrom', 'evalTo', 'search']
      .filter((field) => Boolean(filters[field])).length;

  const openPartner = (row) => {
    setSelectedPartner(row);
    setIsPartnerEvaluationOpen(true);
  };

  const columns = [
    { field: 'partenaire', headerName: 'Partenaire', minWidth: 230, flex: 1.2 },
    { field: 'directions', headerName: 'Direction(s)', minWidth: 150, flex: 0.8 },
    {
      field: 'situation', headerName: 'Situation', minWidth: 125,
      renderCell: ({ value }) => <Chip size="small" label={value || '—'} className={`${classes.badge} ${value === 'EN COURS' ? classes.badgeSituationEncours : value === 'RESILIE' ? classes.badgeSituationResilie : classes.badgeSituationFin}`} />,
    },
    { field: 'sourceDb', headerName: 'Base source', minWidth: 170, flex: 0.8, renderCell: ({ value }) => formatPartnerSource(value) },
    { field: 'nbMarches', headerName: 'Marchés', type: 'number', minWidth: 90 },
    { field: 'totalBeneficiaires', headerName: 'Bénéficiaires', type: 'number', minWidth: 115, renderCell: ({ value }) => formatCount(value) },
    {
      field: 'evaluation', headerName: 'Évaluation', minWidth: 150,
      renderCell: ({ value }) => {
        const meta = getEvaluationMeta(value);
        const style = { good: classes.badgeEvalGood, average: classes.badgeEvalAverage, poor: classes.badgeEvalPoor, acceptable: classes.badgeEvalAcceptable, missing: classes.badgeMissing }[meta.key];
        return <span className={`${classes.badge} ${style}`}>{meta.label}</span>;
      },
    },
    { field: 'evaluationDate', headerName: 'Date évaluation', minWidth: 130, renderCell: ({ value }) => formatDate(value) },
    {
      field: 'fiche', headerName: 'Fiche', width: 80, sortable: false, filterable: false, align: 'center', headerAlign: 'center',
      renderCell: ({ row }) => <Tooltip title="Afficher la fiche"><IconButton size="small" className={classes.ficheButton} onClick={(event) => { event.stopPropagation(); openPartner(row); }}>📋</IconButton></Tooltip>,
    },
  ];

  const multiFilter = (label, value, field, options) => (
    <Autocomplete multiple disableCloseOnSelect size="small" options={options} value={value}
      onChange={(_, nextValue) => setFilters((current) => ({ ...current, [field]: nextValue }))}
      renderInput={(params) => <TextField {...params} label={label} variant="outlined" sx={partnerFilterFieldSx} />} />
  );

  return <PartnerMainContainer>
    <LoadingSimpleBackdrop openLoadingSimpleBackdrop={isLoading} />
    <TitlePartner title="Évaluations" sub={`(${rows.length} partenaires)`} icon="📊" />
    {loadError && <Alert severity="error" sx={{ mb: 1.5 }}>{loadError}</Alert>}
    {missingEvaluationCount > 0 && <Alert severity="warning" sx={{ mb: 1.5 }}><strong>{missingEvaluationCount}</strong> partenaire(s) en cours sans évaluation.</Alert>}

    <Paper elevation={0} sx={{ border: '1px solid #cbd8e6', borderRadius: '8px', mb: 2, p: 1.25 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, justifyContent: 'space-between', mb: filtersVisible ? 1.5 : 0 }}>
        <Box><Typography color="#52677c" fontSize={10} fontWeight={700} letterSpacing="0.05em">FILTRES</Typography><Typography color="#667085" fontSize={10}>{activeFiltersCount} critère(s) actif(s)</Typography></Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          {filtersVisible && <Button disabled={activeFiltersCount === 0} onClick={() => setFilters(defaultFilters)} size="small" startIcon={<RestartAltIcon />} sx={{ ...partnerFilterButtonSx, borderColor: '#9fb1c3', color: partnerTableColors.dark }} variant="outlined">Réinitialiser</Button>}
          <Button onClick={() => setFiltersVisible((visible) => !visible)} size="small" startIcon={filtersVisible ? <FilterAltOffIcon /> : <FilterAltIcon />} sx={{ ...partnerFilterButtonSx, borderColor: '#9fb1c3', color: partnerTableColors.dark }} variant="outlined">{filtersVisible ? 'Masquer filtres' : 'Afficher filtres'}</Button>
        </Box>
      </Box>
      <Collapse in={filtersVisible} timeout="auto" unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Grid container spacing={1.25}>
            <Grid item xs={12} sm={6} md={4}>{multiFilter('Direction', filters.directions, 'directions', directionOptions)}</Grid>
            <Grid item xs={12} sm={6} md={4}>{multiFilter('Financement', filters.financements, 'financements', fundingOptions)}</Grid>
            <Grid item xs={12} sm={6} md={4}>{multiFilter('Situation', filters.situations, 'situations', situationOptions)}</Grid>
          </Grid>
          <Grid container spacing={1.25}>
            <Grid item xs={12} md={4}><TextField fullWidth size="small" placeholder="Partenaire..." value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} sx={partnerFilterFieldSx} InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: '#667085' }} /> }} /></Grid>
            <Grid item xs={12} sm={6} md={2}><TextField select fullWidth label="Résultat" size="small" value={filters.result} onChange={(event) => setFilters((current) => ({ ...current, result: event.target.value }))} sx={partnerFilterFieldSx}><MenuItem value="">Tous</MenuItem><MenuItem value="MANQUANT">Manquant</MenuItem><MenuItem value="BONNE PERFORMANCE">Bonne performance</MenuItem><MenuItem value="PERFORMANCE MOYENNE">Performance moyenne</MenuItem><MenuItem value="PERFORMANCE INSUFFISANTE">Performance insuffisante</MenuItem><MenuItem value="ACCEPTABLE">Acceptable</MenuItem></TextField></Grid>
            <Grid item xs={12} sm={6} md={2}><TextField select fullWidth label="Situation vue" size="small" value={filters.scopeSituation} onChange={(event) => setFilters((current) => ({ ...current, scopeSituation: event.target.value }))} sx={partnerFilterFieldSx}><MenuItem value="">Toutes</MenuItem><MenuItem value="EN COURS">En cours seulement</MenuItem></TextField></Grid>
            <Grid item xs={12} sm={6} md={2}><TextField select fullWidth label="Vue" size="small" value={filters.view} onChange={(event) => setFilters((current) => ({ ...current, view: event.target.value }))} sx={partnerFilterFieldSx}><MenuItem value="cards">Fiches</MenuItem><MenuItem value="table">Tableau</MenuItem></TextField></Grid>
            <Grid item xs={12} sm={6} md={2}><TextField select fullWidth label="Trier par" size="small" value={filters.sortBy} onChange={(event) => setFilters((current) => ({ ...current, sortBy: event.target.value }))} sx={partnerFilterFieldSx}><MenuItem value="name">Nom</MenuItem><MenuItem value="evalDate">Date évaluation</MenuItem><MenuItem value="nbMarches">Nombre de marchés</MenuItem></TextField></Grid>
          </Grid>
          <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 1.25 }}>
            <Box sx={{ alignItems: 'center', border: `1px solid ${partnerTableColors.border}`, borderRadius: '6px', display: 'flex', flex: 1, flexWrap: 'wrap', gap: 1, p: 1, width: '100%' }}><Typography color="#52677c" fontSize={11} fontWeight={700}>Période du contrat :</Typography><TextField type="date" size="small" value={filters.contractFrom} onChange={(event) => setFilters((current) => ({ ...current, contractFrom: event.target.value }))} sx={{ ...partnerFilterFieldSx, width: { xs: '100%', sm: 165 } }} /><Typography>→</Typography><TextField type="date" size="small" value={filters.contractTo} onChange={(event) => setFilters((current) => ({ ...current, contractTo: event.target.value }))} sx={{ ...partnerFilterFieldSx, width: { xs: '100%', sm: 165 } }} /></Box>
            <Box sx={{ alignItems: 'center', border: `1px solid ${partnerTableColors.border}`, borderRadius: '6px', display: 'flex', flex: 1, flexWrap: 'wrap', gap: 1, p: 1, width: '100%' }}><Typography color="#52677c" fontSize={11} fontWeight={700}>Période d’évaluation :</Typography><TextField type="date" size="small" value={filters.evalFrom} onChange={(event) => setFilters((current) => ({ ...current, evalFrom: event.target.value }))} sx={{ ...partnerFilterFieldSx, width: { xs: '100%', sm: 165 } }} /><Typography>→</Typography><TextField type="date" size="small" value={filters.evalTo} onChange={(event) => setFilters((current) => ({ ...current, evalTo: event.target.value }))} sx={{ ...partnerFilterFieldSx, width: { xs: '100%', sm: 165 } }} /></Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>

    {filters.view === 'cards' ? <EvalutionFiche rows={rows} classes={classes} formatDate={formatDate} getEvaluationMeta={getEvaluationMeta} onOpenFiche={openPartner} /> : (
      <Paper elevation={0} sx={{ border: '1px solid #cbd8e6', borderRadius: '8px', overflow: 'hidden' }}>
        <Box sx={{ alignItems: 'center', bgcolor: '#f8fbff', borderBottom: `1px solid ${partnerTableColors.border}`, display: 'flex', justifyContent: 'space-between', px: 1.75, py: 1.5 }}><Typography sx={{ color: '#1f2a44', fontSize: 13, fontWeight: 600 }}>Liste des évaluations — 1 ligne = 1 partenaire</Typography><Typography sx={{ color: '#4b6488', fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{rows.length} partenaire(s)</Typography></Box>
        <Box sx={{ height: 560 }}><DataGrid rows={rows} columns={columns} columnHeaderHeight={48} disableRowSelectionOnClick localeText={frFR.components.MuiDataGrid.defaultProps.localeText} initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }} onRowDoubleClick={({ row }) => openPartner(row)} pageSizeOptions={[10, 15, 30, 50]} rowHeight={48} sx={partnerDataGridSx} /></Box>
      </Paper>
    )}

    <PartnerEvalution open={isPartnerEvaluationOpen} onClose={() => setIsPartnerEvaluationOpen(false)} partnerId={selectedPartner?.partenaireId || null} partnerPid={selectedPartner?.pid || ''} partnerName={selectedPartner?.partenaire || ''} sourceDb={selectedPartner?.sourceDb || ''} readOnly={selectedPartner?.readOnly || false} />
  </PartnerMainContainer>;
};

export default Evaluation;
